import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { getApiErrorDetail } from '@hetivo/hetivo-lib-front-utils';
import { LOCATION_STATUS } from '@hetivo/hetivo-lib-types';
import type { LocationStatus } from '@hetivo/hetivo-lib-types';

import type { CreateLocationFormValues } from '../schemas/create-location.schema';
import { hasPhoneData, normaliseGeocodingScore, toGeoCoordinates } from '../utils/location-form.utils';
import { useCreateLocation } from './use-create-location';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { CreateLocationFormValues };

export interface UseCreateLocationFormOptions {
  defaultTerritoryId?: string;
  onSuccess: () => void;
}

export interface UseCreateLocationFormReturn {
  form: UseFormReturn<CreateLocationFormValues>;
  isPending: boolean;
  onSubmit: (data: CreateLocationFormValues) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const phoneSchema = z
  .object({ dialCode: z.string(), number: z.string() })
  .refine((p) => /^\d{4,15}$/.test(p.number), { message: 'Phone number must contain 4 to 15 digits' })
  .nullable()
  .optional();

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCreateLocationForm({
  defaultTerritoryId,
  onSuccess,
}: UseCreateLocationFormOptions): UseCreateLocationFormReturn {
  const { t } = useTranslation('settings');
  const createLocation = useCreateLocation();

  const schema = useMemo(
    () =>
      z.object({
        address: z.object({
          additionalInformations: z.string().optional(),
          city: z.string().trim().min(1, t('locations.create.validation.cityRequired')),
          complement: z.string().optional(),
          country: z.string().trim().min(1, t('locations.create.validation.countryRequired')),
          digicode: z.string().optional(),
          floor: z.string().optional(),
          hasElevator: z.boolean().optional(),
          houseNumber: z.string().optional(),
          postalCode: z.string().trim().optional(),
          street: z.string().trim().optional(),
          title: z.string().optional(),
        }),
        color: z.string().min(1, t('locations.create.validation.colorRequired')),
        coordinates: z
          .object({
            x: z.number().optional(),
            y: z.number().optional(),
          })
          .optional(),
        customerSupportPhone: phoneSchema,
        geocodingScore: z.number().min(1, t('locations.create.validation.geocodingRequired')),
        geocodingSource: z.enum(['autocomplete', 'manual']).optional(),
        name: z.string().trim().min(1, t('locations.create.validation.nameRequired')),
        phone: z
          .object({
            dialCode: z.string().min(1, t('locations.create.validation.phoneRequired')),
            number: z.string().min(1, t('locations.create.validation.phoneRequired')),
          })
          .refine((p) => /^\d{4,15}$/.test(p.number), t('locations.create.validation.phoneInvalid')),
        status: z.enum(Object.values(LOCATION_STATUS) as [LocationStatus, ...LocationStatus[]]),
        territoryId: z.string().min(1, t('locations.create.validation.territoryRequired')),
      }),
    [t],
  );

  const form = useForm<CreateLocationFormValues>({
    defaultValues: {
      address: {
        additionalInformations: '',
        city: '',
        complement: '',
        country: '',
        digicode: '',
        floor: '',
        hasElevator: false,
        houseNumber: '',
        postalCode: '',
        street: '',
        title: '',
      },
      color: '#64C466',
      coordinates: {
        x: undefined,
        y: undefined,
      },
      customerSupportPhone: null,
      geocodingScore: undefined,
      geocodingSource: undefined,
      name: '',
      phone: { dialCode: '+33', number: '' },
      status: LOCATION_STATUS.ACTIVE,
      territoryId: defaultTerritoryId ?? '',
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  // ─── Submit ───────────────────────────────────────────────────────────────

  function onSubmit(data: CreateLocationFormValues): void {
    if (createLocation.isPending) return;

    const geocodingScore = normaliseGeocodingScore(data.geocodingScore);

    createLocation
      .mutateAsync({
        address: data.address,
        color: data.color,
        ...(toGeoCoordinates(data.coordinates) !== undefined
          ? { coordinates: toGeoCoordinates(data.coordinates) }
          : {}),
        ...(hasPhoneData(data.customerSupportPhone) ? { customerSupportPhone: data.customerSupportPhone } : {}),
        ...(geocodingScore !== undefined ? { geocodingScore } : {}),
        geocodingSource: data.geocodingSource,
        name: data.name,
        phone: data.phone,
        status: data.status,
        territoryId: data.territoryId,
      })
      .then(
        () => {
          toast.success(t('locations.create.success'));
          onSuccess();
        },
        (error: unknown) => {
          const detail = getApiErrorDetail(error);
          if (detail.statusCode === 409) {
            form.setError('name', { message: t('locations.create.validation.nameExists') });
            return;
          }
          toast.error(detail.message || t('locations.create.error'));
        },
      );
  }

  return {
    form,
    isPending: createLocation.isPending,
    onSubmit,
  };
}
